import { useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { hasFamilyRole } from '@steward-app/steward-types';
import { createApiClient } from '../api/client';
import type { MeResponse } from '../api/types';
import { useAccessToken } from '../auth/useAuth';
import { ME_QUERY_KEY } from '../lib/meQuery';
import { workspaceKey } from '../lib/workspaceQueryKey';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import {
  hasOpenOnboardingRequest, openOnboardingRequests, snoozeOnboardingRequest, visibleOnboardingRequests,
  type OpenOnboardingRequest,
} from './requests';

/**
 * The ONE cache entry for the active family's onboarding requests (steward-app#1426/#1427).
 *
 * Three surfaces read it: the notice on People, the Dashboard card and the People nav dot. One
 * key is what keeps them from disagreeing about the same request, and what lets the notice's
 * "Done, send to my advisor" clear the card and the dot with the single invalidation it already
 * makes.
 */
export const onboardingRequestsQueryKey = (workspaceId: string | null) =>
  workspaceKey('onboarding-requests', workspaceId);

/**
 * Matches `useFeedbackQuery`'s window, and for the same reason: this query is mounted from
 * `Layout`, so it runs on every app load and every window focus, and the façade route behind it
 * reads `/me`, three entity lists and the firm directory. A family confirming refreshes it at
 * once through the invalidation; a firm re-issuing reaches an open tab on the first mount or
 * window focus after this window has passed.
 */
const ONBOARDING_REQUESTS_STALE_TIME = 5 * 60_000;

/**
 * `GET /onboarding-requests` for the active workspace, raw.
 *
 * **Fetched only for a FAMILY member of the active workspace** (`hasFamilyRole`, the same
 * predicate the façade's `requireFamilyMember` refuses with). Anyone else gets a 403 from that
 * route, so asking would spend a request on every page load of every advisor looking in on a
 * client and every firm staff member in their firm workspace, for an answer that is always "no".
 * This is a saving, not the gate: the façade still refuses a non-family caller whatever this
 * says. A disabled query never succeeds, so every consumer below renders nothing for them.
 *
 * `retry: false`, as the notice had it: a 404 is a façade older than the route (steward-app#319)
 * and a 403 will not change on retry.
 */
export function useOnboardingRequestsQuery() {
  const token = useAccessToken();
  const { activeWorkspaceId, activeMembership } = useWorkspace();
  const family = hasFamilyRole(activeMembership?.roleIds ?? []);
  return useQuery({
    queryKey: onboardingRequestsQueryKey(activeWorkspaceId),
    queryFn: () => createApiClient(token!).get<unknown>('/onboarding-requests'),
    enabled: !!token && !!activeWorkspaceId && family,
    staleTime: ONBOARDING_REQUESTS_STALE_TIME,
    retry: false,
  });
}

/**
 * Whether the People nav dot shows. `false` until the read has SUCCEEDED, and for any failure:
 * a dot that cannot be cleared is worse than a missing one, and the notice on People takes the
 * same posture.
 */
export function useOnboardingRequestWaiting(): boolean {
  const q = useOnboardingRequestsQuery();
  return q.isSuccess && hasOpenOnboardingRequest(q.data);
}

export interface OnboardingRequestReminders {
  /** The open requests the Dashboard card shows now, after "Remind me later". */
  visible: OpenOnboardingRequest[];
  /** Hide this request's card for three days, or until the firm asks again. Takes the request
   *  off the card that was pressed, so the issuance stored is the one the person saw. */
  remindLater: (request: OpenOnboardingRequest) => void;
}

/**
 * The Dashboard card's state: the open requests, less the ones "Remind me later" is hiding, and
 * the action that hides one.
 *
 * The snooze lives in the `/me` preferences blob as `onboardingSnoozed`, not in localStorage:
 * localStorage is per browser, not per person, so on a shared family computer one person's
 * "later" would quieten the card for everyone.
 */
export function useOnboardingRequestReminders(now: number = Date.now()): OnboardingRequestReminders {
  const q = useOnboardingRequestsQuery();
  const { profile, setPreferences, activeWorkspaceId } = useWorkspace();
  const queryClient = useQueryClient();
  const snoozes = profile?.preferences?.onboardingSnoozed;

  const visible = useMemo(() => {
    if (!q.isSuccess) return [];
    const open = openOnboardingRequests(q.data);
    return open === null ? [] : visibleOnboardingRequests(open, snoozes, activeWorkspaceId, now);
  }, [q.isSuccess, q.data, snoozes, activeWorkspaceId, now]);

  /**
   * Tail of the `PUT /me` chain, for the reason `useFeedbackUnread`'s `markSeen` gives: appcore
   * replaces a top-level preference value whole, so two presses whose writes land out of order
   * would let the older body erase the newer snooze.
   */
  const writeQueue = useRef<Promise<void>>(Promise.resolve());

  const remindLater = useCallback((request: OpenOnboardingRequest) => {
    // Captured from the render the press happened in, deliberately, as `markSeen` does: the
    // snooze belongs to the family whose card was pressed, even if the workspace switches
    // before this write runs.
    const workspaceId = activeWorkspaceId;
    if (workspaceId === null) return;
    writeQueue.current = writeQueue.current
      .then(async () => {
        // Read HERE, not from the render that queued the write, so a queued write builds on
        // the one before it rather than on a revision it has already overtaken.
        const current = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY)?.preferences?.onboardingSnoozed;
        // The pressed workspace's open requests ONLY from a read that succeeded. A workspace
        // switch between the press and this write can reset that entry; `null` then tells the
        // pruning it does not know what is open, rather than "nothing is" (which would erase
        // every live snooze in this family).
        const state = queryClient.getQueryState(onboardingRequestsQueryKey(workspaceId));
        const open = state?.status === 'success' ? openOnboardingRequests(state.data) : null;
        const next = snoozeOnboardingRequest(current, open, workspaceId, request, Date.now());
        // `setPreferences` updates `['me']` optimistically, so the card hides at once, and
        // refetches server truth if the PUT fails (including appcore's 400
        // PREFERENCES_TOO_LARGE). A failed snooze therefore brings the card back rather than
        // raising an error over the Dashboard, which is the right cost for a reminder.
        await setPreferences({ onboardingSnoozed: next });
      })
      .catch(() => {});
  }, [queryClient, setPreferences, activeWorkspaceId]);

  return { visible, remindLater };
}
